import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Link as MuiLink,
  Alert,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useCookieConsent } from '../contexts/CookieContext';

export default function CookieConsent() {
  const { setConsent, hasConsented } = useCookieConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tempConsent, setTempConsent] = useState({
    analytics: true,
    advertising: true,
    preferences: true,
  });

  useEffect(() => {
    // Show banner if user hasn't made a choice yet
    if (!hasConsented) {
      setShowBanner(true);
    }
  }, [hasConsented]);

  const handleAcceptAll = () => {
    setConsent({
      analytics: true,
      advertising: true,
      preferences: true,
    });
    setShowBanner(false);
  };

  const handleDecline = () => {
    setConsent({
      analytics: false,
      advertising: false,
      preferences: false,
    });
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    setConsent(tempConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSettings = () => {
    setShowSettings(true);
  };

  if (!showBanner) {
    return null;
  }

  if (showSettings) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            p: 3,
            borderRadius: 2,
          }}
        >
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6" component="h2">
                Cookie Settings
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Choose which types of cookies you want to allow. Essential cookies are always enabled as they are necessary for the website to function.
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.preferences}
                      onChange={(e) => setTempConsent(prev => ({ ...prev, preferences: e.target.checked }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label="Preference Cookies"
                />
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  These cookies remember your preferences like dark/light mode and party mode settings.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.analytics}
                      onChange={(e) => setTempConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label="Analytics Cookies"
                />
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  These cookies help us understand how visitors use our website to improve performance and user experience.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.advertising}
                      onChange={(e) => setTempConsent(prev => ({ ...prev, advertising: e.target.checked }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label="Advertising Cookies"
                />
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  These cookies are used to display relevant advertisements and help support the free development of ByteScan.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              You can change these settings at any time by visiting our{' '}
              <MuiLink href="/cookie-policy.html" target="_blank" rel="noopener noreferrer">
                Cookie Policy
              </MuiLink>
              .
            </Alert>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowSettings(false)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveSettings}
                sx={{ minWidth: '120px' }}
              >
                Save Settings
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        p: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          p: 3,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CookieIcon color="primary" />
            <Typography variant="h6" component="h2">
              Cookie Consent
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            We use cookies to enhance your experience on ByteScan. These cookies help us:
          </Typography>

          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Remember your preferences (dark/light mode)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Analyze website usage to improve our tools
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Display relevant advertisements
            </Typography>
          </Box>

          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            By clicking "Accept All", you consent to our use of cookies. You can learn more in our{' '}
            <MuiLink href="/cookie-policy.html" target="_blank" rel="noopener noreferrer">
              Cookie Policy
            </MuiLink>
            .
          </Alert>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleSettings}
              startIcon={<SettingsIcon />}
            >
              Cookie Settings
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDecline}
              color="error"
            >
              Decline
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              sx={{ minWidth: '120px' }}
            >
              Accept All
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
} 